// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/billing/FraudSettingsPage.tsx
//
// Phase 3.2 Task 34 — Admin Fraud Settings page.
//
// Per CONTEXT.md (velocity / suspicious / cooling — global config
// + per-merchant overrides). Covers goal G-FRAU.
//
// Endpoints (Per API-CONTRACTS.md L1626-1838):
//   GET    /api/v1/admin/billing-settings/fraud
//   PATCH  /api/v1/admin/billing-settings/fraud
//   GET    /api/v1/admin/merchants/:id/fraud-overrides
//   PUT    /api/v1/admin/merchants/:id/fraud-overrides
//
// Design: admin-fraud-settings.png — title + subtitle, two cards
// (Velocity Limits / Suspicious Topup Flag) with 2-col field grids,
// orange "Save Changes" + secondary "Discard" footer.

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MerchantPicker,
  useToast,
  PageHeader,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import {
  envelopeError,
  newIdempotencyKey,
  unwrap,
  type LooseClient,
} from './billing-shared.js';
import { useTranslation } from '../../i18n/useTranslation.js';

interface FraudConfig {
  velocity: {
    max_topup_requests_per_day: number;
    max_withdraw_amount_per_day_cents: number;
  };
  suspicious: {
    topup_count_per_day: number;
    total_amount_per_day_cents: number;
  };
  cooling_period_days: number;
}

interface MerchantOverrides {
  velocity_override: {
    max_topup_requests_per_day: number | null;
    max_withdraw_amount_per_day_cents: number | null;
  } | null;
  cooling_period_override: number | null;
}

const DEFAULT_FORM: FraudConfig = {
  velocity: {
    max_topup_requests_per_day: 10,
    max_withdraw_amount_per_day_cents: 500_000,
  },
  suspicious: {
    topup_count_per_day: 5,
    total_amount_per_day_cents: 1_000_000,
  },
  cooling_period_days: 3,
};

export function FraudSettingsPage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const qc = useQueryClient();

  // ---- Global fraud config ----
  const fraudQuery = useQuery({
    queryKey: ['admin', 'fraud-config'],
    queryFn: async (): Promise<FraudConfig> => {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.GET(
        '/api/v1/admin/billing-settings/fraud',
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<FraudConfig>(data);
    },
  });

  const [form, setForm] = useState<FraudConfig>(DEFAULT_FORM);

  useEffect(() => {
    if (fraudQuery.data) {
      setForm(fraudQuery.data);
    }
  }, [fraudQuery.data]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(fraudQuery.data ?? DEFAULT_FORM),
    [form, fraudQuery.data],
  );

  const saveMutation = useMutation({
    mutationFn: async (body: FraudConfig): Promise<FraudConfig> => {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.PATCH(
        '/api/v1/admin/billing-settings/fraud',
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body,
        },
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<FraudConfig>(data);
    },
    onSuccess: (saved) => {
      qc.setQueryData(['admin', 'fraud-config'], saved);
      toast({ variant: 'success', title: t('fraud.toast.savedTitle') });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('fraud.toast.saveFailedTitle'),
        description: err instanceof Error ? err.message : t('fraud.toast.networkError'),
      });
    },
  });

  function handleNumber(
    path: 'velocity.max_topup_requests_per_day'
      | 'velocity.max_withdraw_amount_per_day_cents'
      | 'suspicious.topup_count_per_day'
      | 'suspicious.total_amount_per_day_cents'
      | 'cooling_period_days',
    raw: string,
  ): void {
    // Empty string → keep last numeric to avoid Zod-positive failure on submit.
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return;
    setForm((prev) => {
      const next = { ...prev, velocity: { ...prev.velocity }, suspicious: { ...prev.suspicious } };
      switch (path) {
        case 'velocity.max_topup_requests_per_day':
          next.velocity.max_topup_requests_per_day = Math.floor(n);
          break;
        case 'velocity.max_withdraw_amount_per_day_cents':
          // UI shows USD whole dollars — store cents = dollars * 100.
          next.velocity.max_withdraw_amount_per_day_cents = Math.floor(n) * 100;
          break;
        case 'suspicious.topup_count_per_day':
          next.suspicious.topup_count_per_day = Math.floor(n);
          break;
        case 'suspicious.total_amount_per_day_cents':
          next.suspicious.total_amount_per_day_cents = Math.floor(n) * 100;
          break;
        case 'cooling_period_days':
          next.cooling_period_days = Math.floor(n);
          break;
      }
      return next;
    });
  }

  // ---- Per-merchant override drawer ----
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [merchantId, setMerchantId] = useState('');
  const [merchantLabel, setMerchantLabel] = useState<string | null>(null);
  const [override, setOverride] = useState<MerchantOverrides>({
    velocity_override: null,
    cooling_period_override: null,
  });

  const overrideQuery = useQuery({
    queryKey: ['admin', 'fraud-overrides', merchantId],
    enabled: overrideOpen && /^[a-f0-9]{24}$/.test(merchantId),
    queryFn: async (): Promise<MerchantOverrides> => {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.GET(
        `/api/v1/admin/merchants/${encodeURIComponent(merchantId)}/fraud-overrides`,
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<MerchantOverrides>(data);
    },
  });

  useEffect(() => {
    if (overrideQuery.data) setOverride(overrideQuery.data);
  }, [overrideQuery.data]);

  const overrideMutation = useMutation({
    mutationFn: async (body: MerchantOverrides): Promise<MerchantOverrides> => {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.PUT(
        `/api/v1/admin/merchants/${encodeURIComponent(merchantId)}/fraud-overrides`,
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body,
        },
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<MerchantOverrides>(data);
    },
    onSuccess: () => {
      toast({ variant: 'success', title: t('fraud.toast.overrideSavedTitle') });
      setOverrideOpen(false);
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('fraud.toast.overrideFailedTitle'),
        description: err instanceof Error ? err.message : t('fraud.toast.networkError'),
      });
    },
  });

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8">
      <PageHeader title={t('fraud.page.heading')} subtitle={t('fraud.page.subtitle')} />

      {fraudQuery.isLoading ? (
        <p className="text-sm text-pw-text-muted">{t('fraud.loading')}</p>
      ) : fraudQuery.isError ? (
        <p className="text-sm text-red-600 dark:text-red-300">
          {t('fraud.loadFailedPrefix')}{' '}
          {fraudQuery.error instanceof Error ? fraudQuery.error.message : t('fraud.unknown')}
        </p>
      ) : (
        <>
          {/* ---- Velocity Limits ---- */}
          <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
            <h2 className="text-lg font-semibold text-pw-text">{t('fraud.velocity.heading')}</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <FieldNumber
                id="vel-withdraw"
                label={t('fraud.velocity.maxWithdraw')}
                value={form.velocity.max_withdraw_amount_per_day_cents / 100}
                onChange={(raw) =>
                  handleNumber('velocity.max_withdraw_amount_per_day_cents', raw)
                }
                hint={t('fraud.velocity.maxWithdrawHint')}
              />
              <FieldNumber
                id="vel-topup"
                label={t('fraud.velocity.maxTopup')}
                value={form.velocity.max_topup_requests_per_day}
                onChange={(raw) => handleNumber('velocity.max_topup_requests_per_day', raw)}
              />
            </div>
          </section>

          {/* ---- Suspicious Topup Flag ---- */}
          <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
            <h2 className="text-lg font-semibold text-pw-text">{t('fraud.suspicious.heading')}</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <FieldNumber
                id="susp-count"
                label={t('fraud.suspicious.countThreshold')}
                value={form.suspicious.topup_count_per_day}
                onChange={(raw) => handleNumber('suspicious.topup_count_per_day', raw)}
              />
              <FieldNumber
                id="susp-amount"
                label={t('fraud.suspicious.amountThreshold')}
                value={form.suspicious.total_amount_per_day_cents / 100}
                onChange={(raw) => handleNumber('suspicious.total_amount_per_day_cents', raw)}
              />
            </div>
          </section>

          {/* ---- Cooling period + per-merchant overrides ---- */}
          <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
            <h2 className="text-lg font-semibold text-pw-text">{t('fraud.cooling.heading')}</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <FieldNumber
                id="cooling-days"
                label={t('fraud.cooling.defaultDays')}
                value={form.cooling_period_days}
                onChange={(raw) => handleNumber('cooling_period_days', raw)}
                hint={t('fraud.cooling.defaultDaysHint')}
              />
            </div>
            <div className="mt-6 rounded-lg border border-pw-border bg-pw-surface p-4">
              <h3 className="text-sm font-semibold text-pw-text">{t('fraud.overrides.heading')}</h3>
              <p className="mt-1 text-xs text-pw-text-muted">
                {t('fraud.overrides.description')}
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <div className="flex-1">
                  <MerchantPicker
                    value={merchantId || null}
                    valueLabel={merchantLabel}
                    onChange={(id, label) => {
                      setMerchantId(id ?? '');
                      setMerchantLabel(label);
                    }}
                    placeholder={t('fraud.overrides.merchantPlaceholder')}
                    testId="merchant-search"
                  />
                </div>
                <Button
                  variant="secondary"
                  disabled={!merchantId}
                  onClick={() => {
                    if (!merchantId) {
                      toast({
                        variant: 'error',
                        title: t('fraud.toast.noMerchantTitle'),
                        description: t('fraud.toast.noMerchantDesc'),
                      });
                      return;
                    }
                    setOverride({
                      velocity_override: null,
                      cooling_period_override: null,
                    });
                    setOverrideOpen(true);
                  }}
                >
                  {t('fraud.overrides.open')}
                </Button>
              </div>
            </div>
          </section>

          {/* ---- Footer actions ---- */}
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="secondary"
              disabled={!dirty || saveMutation.isPending}
              onClick={() => setForm(fraudQuery.data ?? DEFAULT_FORM)}
              data-testid="fraud-discard"
            >
              {t('fraud.discard')}
            </Button>
            <Button
              disabled={!dirty || saveMutation.isPending}
              onClick={() => saveMutation.mutate(form)}
              data-testid="fraud-save"
            >
              {saveMutation.isPending ? t('fraud.saving') : t('fraud.saveChanges')}
            </Button>
          </div>
        </>
      )}

      {/* ---- Override drawer (modal-style Dialog) ---- */}
      <Dialog open={overrideOpen} onOpenChange={(o) => !o && setOverrideOpen(false)}>
        <DialogHeader>
          <DialogTitle>{t('fraud.overrides.heading')}</DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <p className="text-xs text-pw-text-muted">
            {t('fraud.overrides.merchantLabel')} <span className="font-mono">{merchantId}</span>
          </p>
          {overrideQuery.isLoading ? (
            <p className="text-sm text-pw-text-muted">{t('fraud.overrides.loading')}</p>
          ) : (
            <>
              <FieldNumber
                id="override-topup"
                label={t('fraud.overrides.maxTopup')}
                value={override.velocity_override?.max_topup_requests_per_day ?? 0}
                onChange={(raw) =>
                  setOverride((p) => ({
                    ...p,
                    velocity_override: {
                      max_topup_requests_per_day: raw === '' ? null : Math.floor(Number(raw)),
                      max_withdraw_amount_per_day_cents:
                        p.velocity_override?.max_withdraw_amount_per_day_cents ?? null,
                    },
                  }))
                }
              />
              <FieldNumber
                id="override-withdraw"
                label={t('fraud.overrides.maxWithdraw')}
                value={(override.velocity_override?.max_withdraw_amount_per_day_cents ?? 0) / 100}
                onChange={(raw) =>
                  setOverride((p) => ({
                    ...p,
                    velocity_override: {
                      max_topup_requests_per_day:
                        p.velocity_override?.max_topup_requests_per_day ?? null,
                      max_withdraw_amount_per_day_cents:
                        raw === '' ? null : Math.floor(Number(raw)) * 100,
                    },
                  }))
                }
              />
              <FieldNumber
                id="override-cooling"
                label={t('fraud.overrides.coolingDays')}
                value={override.cooling_period_override ?? 0}
                onChange={(raw) =>
                  setOverride((p) => ({
                    ...p,
                    cooling_period_override: raw === '' ? null : Math.floor(Number(raw)),
                  }))
                }
              />
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOverrideOpen(false)}>
            {t('fraud.overrides.cancel')}
          </Button>
          <Button
            disabled={overrideMutation.isPending}
            onClick={() => overrideMutation.mutate(override)}
            data-testid="override-save"
          >
            {overrideMutation.isPending ? t('fraud.overrides.saving') : t('fraud.overrides.save')}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

// ---- Local helper — phase-specific numeric input + label pairing ----
// (not promoted to @vollxssp/utils — purely a layout/visual helper bound
// to the design's two-column field grid; not reused outside billing/.)
function FieldNumber({
  id,
  label,
  value,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (raw: string) => void;
  hint?: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-sm font-semibold text-pw-text">
        {label}
      </label>
      <Input
        id={id}
        type="number"
        min={0}
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint ? <p className="text-xs text-pw-text-muted">{hint}</p> : null}
    </div>
  );
}
